import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useChat } from '../../context/ChatContext';
import { chatModel } from '../../data/chatModel';
import { MARKETPLACE_FEES } from '../../utils/marketplaceFees';
import { useNavigate } from 'react-router-dom';

const AuctionManagementModal = ({ auction, isOpen, onClose }) => {
  const { endAuction, confirmWinner, createOrderFromAuction, cancelAuction } = useMarketplace();
  const { findOrCreateChat } = useChat();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [winnerInfo, setWinnerInfo] = useState(null);
  const [shippingAddress, setShippingAddress] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (auction && auction.highestBidder) {
      loadWinnerInfo();
    }
    if (auction && auction.orderId) {
      loadOrderData();
    }
  }, [auction]);

  const loadWinnerInfo = async () => {
    if (!auction?.highestBidder) return;
    
    try {
      const winnerRef = doc(db, 'users', auction.highestBidder);
      const winnerSnap = await getDoc(winnerRef);
      if (winnerSnap.exists()) {
        setWinnerInfo({ id: winnerSnap.id, ...winnerSnap.data() });
      }
    } catch (error) {
      console.error('Error loading winner info:', error);
    }
  };

  const loadOrderData = async () => {
    if (!auction?.orderId) return;
    
    try {
      const orderRef = doc(db, 'orders', auction.orderId);
      const orderSnap = await getDoc(orderRef);
      if (orderSnap.exists()) {
        setOrderData({ id: orderSnap.id, ...orderSnap.data() });
      }
    } catch (error) {
      console.error('Error loading order data:', error);
    }
  };

  const handleEndAuction = async () => {
    if (!window.confirm('คุณต้องการจบประมูลนี้หรือไม่?')) return;
    
    setLoading(true);
    try {
      await endAuction(auction.id);
      alert('จบประมูลสำเร็จ!');
      onClose();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmWinner = async () => {
    if (!auction.highestBidder) {
      alert('ยังไม่มีผู้ชนะประมูล');
      return;
    }
    
    if (!window.confirm('ยืนยันว่าผู้ชนะประมูลคือ ' + (winnerInfo?.displayName || winnerInfo?.email || 'ผู้เสนอราคาสูงสุด') + '?')) return;
    
    setLoading(true);
    try {
      await confirmWinner(auction.id, auction.highestBidder);
      alert('ยืนยันผู้ชนะสำเร็จ!');
      onClose();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!shippingAddress.trim()) {
      alert('กรุณากรอกที่อยู่จัดส่ง');
      return;
    }
    
    setLoading(true);
    try {
      const orderId = await createOrderFromAuction(auction.id, shippingAddress);
      alert('สร้างออเดอร์สำเร็จ!');
      setShowCreateOrderModal(false);
      onClose();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAuction = async () => {
    if (!cancelReason.trim()) {
      alert('กรุณากรอกเหตุผลในการยกเลิก');
      return;
    }
    
    // แสดงข้อความยืนยันพร้อมข้อมูลเกี่ยวกับการคืนเหรียญและค่าปรับ
    const confirmMessage = `คุณต้องการยกเลิกประมูลนี้หรือไม่?\n\n` +
      `⚠️ ระบบจะ:\n` +
      `- คืนเหรียญให้ผู้ประมูลทั้งหมด (${auction.bids ? [...new Set(auction.bids.map(bid => bid.userId))].length : 0} คน)\n` +
      `- หักค่าปรับ ${MARKETPLACE_FEES.CANCEL_AUCTION_PENALTY.default} เหรียญจากคุณ\n\n` +
      `เหตุผล: ${cancelReason}`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    try {
      const result = await cancelAuction(auction.id, cancelReason);
      alert(
        `ยกเลิกประมูลสำเร็จ!\n\n` +
        `✅ คืนเหรียญให้ผู้ประมูล ${result.refundsCount} คนแล้ว\n` +
        `💰 หักค่าปรับ ${result.penaltyPaid} เหรียญจากคุณ`
      );
      setShowCancelModal(false);
      setCancelReason('');
      onClose();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContactWinner = async () => {
    if (!auction.highestBidder) {
      alert('ยังไม่มีผู้ชนะประมูล');
      return;
    }
    
    try {
      const chat = await findOrCreateChat(
        chatModel.types.PRODUCT_INQUIRY,
        auction.highestBidder,
        null,
        auction.productId,
        auction.storeId
      );
      window.location.href = `/marketplace/chat/${chat}`;
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  if (!isOpen || !auction) return null;

  const timeRemaining = auction.endTime
    ? Math.max(0, Math.floor((new Date(auction.endTime.toDate()) - new Date()) / 1000 / 60 / 60))
    : null;
  const isEnded = auction.isEnded || (timeRemaining !== null && timeRemaining <= 0);
  const hasWinner = auction.highestBidder !== null;
  const isWinnerConfirmed = auction.status === 'winner_confirmed';
  const isOrderCreated = auction.status === 'order_created';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">จัดการประมูล</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Auction Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">{auction.productName}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">ราคาเริ่มต้น</p>
                <p className="font-bold text-gray-800">฿{auction.startingPrice?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-600">ราคาปัจจุบัน</p>
                <p className="font-bold text-yellow-600">฿{auction.currentBid?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-600">จำนวนผู้เสนอราคา</p>
                <p className="font-bold text-gray-800">{auction.bids?.length || 0} คน</p>
              </div>
              <div>
                <p className="text-gray-600">สถานะ</p>
                <p className={`font-bold ${
                  isEnded ? 'text-gray-600' : 'text-green-600'
                }`}>
                  {isEnded ? 'จบแล้ว' : `เหลือเวลา ${timeRemaining} ชั่วโมง`}
                </p>
              </div>
            </div>
          </div>

          {/* Winner Info */}
          {hasWinner && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">🏆 ผู้ชนะประมูล</h3>
              {winnerInfo ? (
                <div>
                  <p className="font-bold text-gray-800">
                    {winnerInfo.displayName || winnerInfo.email || 'ผู้ใช้'}
                  </p>
                  <p className="text-sm text-gray-600">ราคาที่ชนะ: ฿{auction.currentBid?.toLocaleString()}</p>
                </div>
              ) : (
                <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {!isEnded && (
              <button
                onClick={handleEndAuction}
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                จบประมูลตอนนี้
              </button>
            )}

            {isEnded && hasWinner && !isWinnerConfirmed && (
              <button
                onClick={handleConfirmWinner}
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ✅ ยืนยันผู้ชนะ
              </button>
            )}

            {isWinnerConfirmed && !isOrderCreated && (
              <>
                <button
                  onClick={() => setShowCreateOrderModal(true)}
                  disabled={loading}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  📦 สร้างออเดอร์
                </button>
                <button
                  onClick={handleContactWinner}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  💬 ติดต่อผู้ชนะ
                </button>
              </>
            )}

            {isOrderCreated && (
              <div 
                onClick={() => {
                  if (auction.orderId) {
                    navigate(`/marketplace/orders/${auction.orderId}`);
                    onClose();
                  }
                }}
                className="bg-green-50 border-2 border-green-300 rounded-lg p-4 cursor-pointer hover:bg-green-100 hover:border-green-400 transition-all shadow-md hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-green-800 font-semibold text-lg">✅ สร้างออเดอร์แล้ว</span>
                    </div>
                    {orderData ? (
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">ออเดอร์ ID:</span> {orderData.id}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">สถานะ:</span> 
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                            orderData.status === 'pending' || orderData.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                            orderData.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                            orderData.status === 'shipping' ? 'bg-purple-100 text-purple-800' :
                            orderData.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {orderData.status === 'pending' || orderData.status === 'pending_payment' ? 'รอชำระเงิน' :
                             orderData.status === 'paid' ? 'ชำระเงินแล้ว' :
                             orderData.status === 'shipping' ? 'กำลังจัดส่ง' :
                             orderData.status === 'delivered' ? 'ส่งแล้ว' :
                             orderData.status}
                          </span>
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">ยอดรวม:</span> ฿{orderData.total?.toLocaleString() || '0'}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-green-600 mt-1">ออเดอร์ ID: {auction.orderId}</p>
                    )}
                  </div>
                  <div className="ml-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2 text-center">คลิกเพื่อดูรายละเอียดออเดอร์</p>
              </div>
            )}

            {!isEnded && (
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={loading}
                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ❌ ยกเลิกประมูล
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">ยกเลิกประมูล</h3>
            
            {/* Warning Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800 font-semibold mb-2">⚠️ สิ่งที่จะเกิดขึ้น:</p>
              <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                <li>คืนเหรียญให้ผู้ประมูลทั้งหมด ({auction.bids ? [...new Set(auction.bids.map(bid => bid.userId))].length : 0} คน)</li>
                <li>หักค่าปรับ {MARKETPLACE_FEES.CANCEL_AUCTION_PENALTY.default} เหรียญจากคุณ</li>
                <li>สินค้าจะกลับไปเป็นสถานะ "พร้อมขาย"</li>
              </ul>
            </div>
            
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เหตุผลในการยกเลิก <span className="text-red-500">*</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="กรุณากรอกเหตุผลในการยกเลิกประมูล"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              rows="4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="flex-1 border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleCancelAuction}
                disabled={loading}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Order Modal */}
      {showCreateOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">สร้างออเดอร์</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                ที่อยู่จัดส่ง
              </label>
              <textarea
                value={shippingAddress}
                onChange={(e) => setShippingAddress(e.target.value)}
                placeholder="กรุณากรอกที่อยู่จัดส่ง"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateOrderModal(false);
                  setShippingAddress('');
                }}
                className="flex-1 border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleCreateOrder}
                disabled={loading}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                สร้างออเดอร์
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionManagementModal;

